import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { FiDownload, FiAlertTriangle, FiCheckCircle, FiXCircle, FiLock } from "react-icons/fi";
import toast from "react-hot-toast";

export default function OneTimeDownload() {
    const { tokenId } = useParams();
    const [status, setStatus] = useState("loading"); // loading, ready, redeemed, expired, invalid
    const [bookTitle, setBookTitle] = useState("");
    const [claiming, setClaiming] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState("");

    useEffect(() => {
        if (!tokenId) {
            setStatus("invalid");
            return;
        }

        async function verifyToken() {
            try {
                const docRef = doc(db, "oneTimeDownloads", tokenId);
                const snap = await getDoc(docRef);

                if (!snap.exists()) {
                    setStatus("invalid");
                    return;
                }

                const data = snap.data();
                setBookTitle(data.title || "Your Digital Book");

                if (data.redeemed) {
                    setStatus("redeemed");
                    return;
                }

                if (data.expiresAt && Date.now() > data.expiresAt) {
                    setStatus("expired");
                    return;
                }

                setStatus("ready");
            } catch (error) {
                console.error("Error verifying download token:", error);
                setStatus("invalid");
            }
        }

        verifyToken();
    }, [tokenId]);

    async function handleClaim() {
        if (claiming || status !== "ready") return;
        setClaiming(true);

        try {
            const redeemFn = httpsCallable(functions, "redeemOneTimeDownload");
            const res = await redeemFn({ tokenId });

            if (res.data?.success && res.data?.url) {
                setDownloadUrl(res.data.url);
                setStatus("redeemed");
                toast.success("Download started!");

                // Trigger file download
                const link = document.createElement("a");
                link.href = res.data.url;
                link.setAttribute("download", ""); // Let storage backend control the filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.error("Failed to redeem download link.");
            }
        } catch (error) {
            console.error("Failed to redeem token:", error);
            toast.error(error.message || "Failed to redeem download link.");
            setStatus("redeemed"); // Set to redeemed as it might have gone through or got used
        } finally {
            setClaiming(false);
        }
    }

    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "70vh",
            padding: "40px 20px",
            fontFamily: "'Inter', system-ui, sans-serif"
        }}>
            <div style={{
                background: "white",
                borderRadius: 16,
                boxShadow: "0 10px 30px rgba(13, 42, 30, 0.08)",
                border: "1px solid #e0ddd8",
                width: "100%",
                maxWidth: 500,
                padding: 40,
                textAlign: "center"
            }}>
                {status === "loading" && (
                    <div>
                        <div className="spinner" style={{
                            width: 40,
                            height: 40,
                            border: "3px solid rgba(13, 74, 53, 0.1)",
                            borderTop: "3px solid var(--green-primary)",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            margin: "0 auto 20px"
                        }}></div>
                        <h3 style={{ color: "#333", margin: 0 }}>Verifying secure link...</h3>
                        <style>{`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}</style>
                    </div>
                )}

                {status === "ready" && (
                    <div>
                        <div style={{
                            width: 60,
                            height: 60,
                            background: "rgba(13, 74, 53, 0.08)",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px"
                        }}>
                            <FiLock size={28} color="var(--green-primary)" />
                        </div>
                        <h2 style={{ color: "#0d4a35", fontSize: 24, margin: "0 0 10px" }}>Secure One-Time Download</h2>
                        <p style={{ color: "#666", fontSize: 15, margin: "0 0 24px" }}>
                            You are ready to download: <br />
                            <strong style={{ color: "#333" }}>{bookTitle}</strong>
                        </p>

                        <div style={{
                            background: "#fff9db",
                            border: "1px solid #fab005",
                            borderRadius: 12,
                            padding: 16,
                            textAlign: "left",
                            marginBottom: 30,
                            display: "flex",
                            gap: 12
                        }}>
                            <FiAlertTriangle size={24} color="#f59f00" style={{ flexShrink: 0, marginTop: 2 }} />
                            <div>
                                <h4 style={{ margin: "0 0 4px", color: "#f59f00", fontSize: 13, fontWeight: 700 }}>Important Notice</h4>
                                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: "#666" }}>
                                    This link is valid for **exactly one download request**. Once you click claim, the link will expire immediately. Make sure you are on the device where you want to store the file.
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={handleClaim}
                            disabled={claiming}
                            style={{
                                width: "100%",
                                padding: "16px 24px",
                                background: "var(--green-primary)",
                                color: "white",
                                border: "none",
                                borderRadius: 8,
                                fontWeight: 700,
                                fontSize: 16,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                cursor: "pointer",
                                transition: "background 0.2s"
                            }}
                            onMouseOver={e => e.currentTarget.style.background = "var(--green-dark)"}
                            onMouseOut={e => e.currentTarget.style.background = "var(--green-primary)"}
                        >
                            <FiDownload size={20} /> {claiming ? "Claiming..." : "Claim & Download Book"}
                        </button>
                    </div>
                )}

                {status === "redeemed" && (
                    <div>
                        <div style={{
                            width: 60,
                            height: 60,
                            background: "rgba(39, 174, 96, 0.08)",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px"
                        }}>
                            <FiCheckCircle size={28} color="#27ae60" />
                        </div>
                        <h2 style={{ color: "#27ae60", fontSize: 24, margin: "0 0 10px" }}>Download Successfully Claimed</h2>
                        <p style={{ color: "#666", fontSize: 15, margin: "0 0 20px" }}>
                            Your download has started for:<br />
                            <strong style={{ color: "#333" }}>{bookTitle}</strong>
                        </p>
                        <p style={{ color: "#888", fontSize: 13, margin: "0 0 24px" }}>
                            This link has now expired and cannot be used again. If your download did not start automatically, you can retry using the button below (this temporary URL will remain active for 15 minutes).
                        </p>
                        
                        {downloadUrl && (
                            <a
                                href={downloadUrl}
                                download
                                style={{
                                    display: "inline-block",
                                    padding: "10px 20px",
                                    background: "#27ae60",
                                    color: "white",
                                    borderRadius: 6,
                                    textDecoration: "none",
                                    fontWeight: 600,
                                    fontSize: 14
                                }}
                            >
                                Retry Download
                            </a>
                        )}
                    </div>
                )}

                {(status === "expired" || status === "invalid") && (
                    <div>
                        <div style={{
                            width: 60,
                            height: 60,
                            background: "rgba(231, 76, 60, 0.08)",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px"
                        }}>
                            <FiXCircle size={28} color="#e74c3c" />
                        </div>
                        <h2 style={{ color: "#e74c3c", fontSize: 24, margin: "0 0 10px" }}>Link Expired or Invalid</h2>
                        <p style={{ color: "#666", fontSize: 15, lineHeight: 1.6, margin: "0 0 24px" }}>
                            {status === "expired" 
                                ? `The one-time secure download window for "${bookTitle}" has expired (valid for 7 days after generation).`
                                : "This secure download link is invalid, has expired, or has already been redeemed."
                            }
                        </p>
                        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
                            If you believe this is an error or need a new download link, please contact customer support at <a href="mailto:support@lbbookspr.com" style={{ color: "var(--accent)", textDecoration: "underline" }}>support@lbbookspr.com</a>.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
